import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, IconButton, Tooltip } from '@material-ui/core';
import { UseMutationOptions } from 'react-query';
import DeleteIcon from '@material-ui/icons/Delete';
import SendIcon from '@material-ui/icons/Send';

import {
  useDeleteClientInvitationsMutation,
  useBulkResendClientInvitationsMutation,
  DeleteClientInvitationsMutationVariables,
  BulkResendClientInvitationsMutationVariables,
  DeleteClientInvitationsMutation,
} from 'api';
import { UseCheckedState } from 'types/app';
import { Alert } from 'components';
import { useNotify } from 'hooks';

enum Action {
  Resend,
  Delete,
  None,
}

type DeleteMutationOptions = UseMutationOptions<
  DeleteClientInvitationsMutation,
  unknown,
  DeleteClientInvitationsMutationVariables
>;

type Variables =
  | DeleteClientInvitationsMutationVariables
  | BulkResendClientInvitationsMutationVariables;

interface Props extends Partial<UseCheckedState> {
  deleteMutationOptions: DeleteMutationOptions;
  count: number;
  variables: Variables;
}

const Actions: FC<Props> = ({ deleteMutationOptions, resetCheckedState, variables, count }) => {
  const notify = useNotify();
  const { t } = useTranslation();
  const [action, setAction] = useState<Action>(Action.None);

  const { mutate: deleteClientInvitationsMutation } = useDeleteClientInvitationsMutation({
    ...deleteMutationOptions,
    onSuccess() {
      notify.info({
        message: t('admin:invitationRevoked', { count }),
      });
    },
  });

  const { mutate: resendClientInvitationsMutation } = useBulkResendClientInvitationsMutation({
    onSuccess() {
      notify.info({
        message: t('admin:invitationResent', { count }),
      });
    },
  });

  function handleCancel() {
    setAction(Action.None);
  }

  function handleBulkDeleteInvitations() {
    deleteClientInvitationsMutation(variables);
    setAction(Action.None);
    resetCheckedState();
  }

  function handleBulkResendInvitations() {
    resendClientInvitationsMutation(variables);
    setAction(Action.None);
    resetCheckedState();
  }

  return (
    <Fragment>
      <Alert
        open={action === Action.Delete}
        onConfirm={handleBulkDeleteInvitations}
        onCancel={handleCancel}
        message={t('admin:revokeInvitations', { count })}
        confirm={t('admin:revoke')}
      />
      <Alert
        open={action === Action.Resend}
        onConfirm={handleBulkResendInvitations}
        onCancel={handleCancel}
        message={t('admin:resendInvitations', { count })}
        confirm={t('admin:resendInvite')}
      />
      <Grid container alignItems="center" justify="flex-end">
        <Grid item>
          <Tooltip placement="top" title={t('admin:revoke')}>
            <IconButton onClick={() => setAction(Action.Delete)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Grid>
        <Grid item>
          <Tooltip placement="top" title={t('admin:resendInvite')}>
            <IconButton onClick={() => setAction(Action.Resend)}>
              <SendIcon />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
    </Fragment>
  );
};

export default Actions;
